import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, useScroll, useInView, animate } from 'framer-motion';
import { ArrowUpRight, ArrowDown } from 'lucide-react';
import { Reveal, Overline, SectionHeader } from '../components/Reveal';
import { MarqueeBand } from '../components/MarqueeBand';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { SERVICES, STATS, INDUSTRIES, WHY_PLANMODO, IMAGES } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
    const sectionRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const cardX = useTransform(mx, [-0.5, 0.5], [-22, 22]);
    const cardY = useTransform(my, [-0.5, 0.5], [-14, 14]);
    const gridX = useTransform(mx, [-0.5, 0.5], [10, -10]);
    const gridY = useTransform(my, [-0.5, 0.5], [8, -8]);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
    const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={sectionRef}
            onMouseMove={onMouseMove}
            className="relative flex min-h-screen items-center overflow-hidden"
            data-testid="hero-section"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img src={IMAGES.commandCenter} alt="Security operations command center" className="h-full w-full object-cover" />
            </motion.div>
            <div className="absolute inset-0 hero-vignette" />
            <motion.div style={{ x: gridX, y: gridY }} className="absolute inset-0 grid-lines opacity-70" />
            <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />

            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-36 pb-24">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
                >
                    <Overline>Planmodo — Resilience &amp; Recovery Partners</Overline>
                </motion.div>

                <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50" data-testid="hero-headline">
                    {['Resilience', 'Beyond Recovery.'].map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: '110%' }}
                                animate={{ y: '0%' }}
                                transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                            >
                                {i === 1 ? (
                                    <>Beyond <span className="text-deepteal">Recovery.</span></>
                                ) : line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                    className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                >
                    Business continuity, disaster recovery, and operational resilience — designed, tested,
                    and proven by practitioners with over a decade of front-line experience across global organizations.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                    className="mt-10 flex flex-col gap-4 sm:flex-row"
                >
                    <Link
                        to="/contact"
                        data-testid="hero-cta-consultation"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-glow px-8 py-4 text-sm font-semibold text-ink transition-transform duration-200 hover:scale-105 hover:shadow-xl hover:shadow-glow/25"
                    >
                        Schedule a Consultation <ArrowUpRight size={16} />
                    </Link>
                    <Link
                        to="/services"
                        data-testid="hero-cta-services"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-500 px-8 py-4 text-sm font-semibold text-slate-100 backdrop-blur-sm transition-colors duration-200 hover:border-deepteal/50 hover:text-deepteal"
                    >
                        Explore Services
                    </Link>
                </motion.div>

                <motion.div
                    style={{ x: cardX, y: cardY }}
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                    className="mt-14 inline-block rounded-2xl border border-slate-700 bg-navy-800/60 p-5 backdrop-blur-xl shadow-2xl shadow-black/40"
                    data-testid="hero-readiness-card"
                >
                    <div className="flex items-center gap-6">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recovery Readiness</p>
                            <p className="mt-1 font-display text-xl font-bold text-slate-50">Proven, not promised</p>
                        </div>
                        <div className="h-10 w-px bg-slate-600" />
                        <div className="flex items-center gap-2.5">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-glow opacity-60" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-glow" />
                            </span>
                            <span className="font-mono text-xs text-deepteal">RTO: MET</span>
                        </div>
                    </div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
                    <ArrowDown size={18} className="text-slate-500" />
                </motion.div>
            </motion.div>
        </section>
    );
};

const Counter = ({ value, suffix }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, { duration: 1.8, ease: EASE, onUpdate: (v) => setDisplay(Math.round(v)) });
        return () => controls.stop();
    }, [inView, value]);
    return <span ref={ref} data-testid="stat-counter">{display}{suffix}</span>;
};

const ServicesPreview = () => (
    <section className="py-20 sm:py-28 lg:py-32" data-testid="services-overview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <SectionHeader
                    overline="What we do"
                    title="Fifteen disciplines. One goal: an organization that bends, never breaks."
                    description="From continuity planning to crisis rehearsal, every service is delivered by practitioners who have done the work — not just studied it. Every discipline follows globally recognized standards — ISO 22301, ISO 27001, and the NIST Cybersecurity Framework — applied with practitioner judgment."
                />
                <Reveal delay={0.15}>
                    <Link to="/services" data-testid="services-view-all-link" className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-deepteal">
                        View all services
                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </Reveal>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {SERVICES.slice(0, 6).map((s, i) => (
                    <Reveal key={s.id} delay={i * 0.07}>
                        <Link
                            to="/services"
                            data-testid={`home-service-card-${s.id}`}
                            className="group flex h-full flex-col rounded-2xl border border-slate-800 bg-navy-800/50 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-deepteal/30 hover:bg-navy-800 hover:shadow-xl hover:shadow-glow/5"
                        >
                            <div className="flex items-center justify-between">
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-deepteal/10 border border-deepteal/20 text-deepteal">
                                    <s.icon size={20} />
                                </span>
                                <span className="font-mono text-xs text-slate-500">{s.num}</span>
                            </div>
                            <h3 className="mt-6 font-display text-lg sm:text-xl font-semibold text-slate-50">{s.title}</h3>
                            <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{s.short}</p>
                            <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-deepteal/80 transition-colors group-hover:text-deepteal">
                                Learn more <ArrowUpRight size={13} />
                            </span>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const WhyPlanmodo = () => (
    <section className="bg-[#F8FAFC] py-20 sm:py-28 lg:py-32" data-testid="why-planmodo-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                dark={false}
                overline="Why Planmodo"
                title="Resilience advice is easy to find. Practitioners are not."
            />
            <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-[#E2E8F0] md:grid-cols-2">
                {WHY_PLANMODO.map((item, i) => (
                    <Reveal key={item.title} delay={i * 0.08} className="bg-white">
                        <div className="h-full p-8 sm:p-10" data-testid={`why-card-${i}`}>
                            <span className="font-mono text-xs text-deepteal">{String(i + 1).padStart(2, '0')}</span>
                            <h3 className="mt-4 font-display text-xl font-semibold text-ink">{item.title}</h3>
                            <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#475569]">{item.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const StatsBand = () => (
    <section className="relative overflow-hidden py-20 sm:py-28" data-testid="stats-section">
        <div className="absolute inset-0">
            <img src={IMAGES.dataCenter} alt="" className="h-full w-full object-cover opacity-15" />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/80 to-navy-900" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
                {STATS.map((stat, i) => (
                    <Reveal key={stat.label} delay={i * 0.08}>
                        <div className="text-center" data-testid={`stat-${i}`}>
                            <p className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-deepteal">
                                <Counter value={stat.value} suffix={stat.suffix} />
                            </p>
                            <p className="mx-auto mt-3 max-w-[12rem] text-xs sm:text-sm leading-relaxed text-slate-400">{stat.label}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const IndustriesSpotlight = () => (
    <section className="py-20 sm:py-28 lg:py-32" data-testid="industries-spotlight-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                overline="Industry expertise"
                title="Disruption looks different in every sector. So does recovery."
                description="We have supported organizations across eight major industries — each with its own regulators, dependencies, and definition of critical."
            />
            <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
                {INDUSTRIES.map((ind, i) => (
                    <Reveal key={ind.id} delay={i * 0.05}>
                        <Link
                            to="/industries"
                            data-testid={`home-industry-card-${ind.id}`}
                            className="group flex h-full flex-col rounded-2xl border border-slate-800 bg-navy-800/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-deepteal/30"
                        >
                            <ind.icon size={22} className="text-deepteal/80 transition-colors group-hover:text-deepteal" />
                            <h3 className="mt-4 font-display text-base sm:text-lg font-semibold text-slate-100">{ind.name}</h3>
                            <p className="mt-2 hidden text-xs leading-relaxed text-slate-500 sm:block">{ind.intro}</p>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function Home() {
    useSEO(
        'Planmodo — Resilience Beyond Recovery | Business Continuity & Disaster Recovery',
        'Practitioner-led business continuity, disaster recovery, crisis management and operational resilience services. Practical outcomes. Measurable readiness.'
    );
    return (
        <>
            <Hero />
            <MarqueeBand />
            <ServicesPreview />
            <WhyPlanmodo />
            <StatsBand />
            <IndustriesSpotlight />
            <CTASection />
        </>
    );
}
